import React, { useState, useMemo } from 'react'
import {
  Drawer, Typography, TextField, Button, Checkbox, InputAdornment
} from '@material-ui/core'
import { Search as SearchIcon } from '@material-ui/icons'
import axios from 'axios'

// Project lifecycle order — early phases first, closeout last
// Project lifecycle order — mobilization/general first, then site prep through finish
const CATEGORY_ORDER = [
  'General', 'Demo', 'Earthwork', 'Irrigation',
  'Hardscape', 'Planting', 'Lighting', 'Water Feature'
]

const ServicePicker = ({ open, onClose, services, projectServices, projectId, url, onServiceAdded, onServiceRemoved }) => {
  const [search, setSearch] = useState('')

  const activeServiceIds = useMemo(() => {
    return new Set((projectServices || []).map(ps => ps.serviceId))
  }, [projectServices])

  const psLookup = useMemo(() => {
    const map = {}
    for (const ps of (projectServices || [])) map[ps.serviceId] = ps
    return map
  }, [projectServices])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return (services || []).filter(s =>
      !q || s.name.toLowerCase().includes(q) || s.serviceId.toLowerCase().includes(q) || (s.category || '').toLowerCase().includes(q)
    )
  }, [services, search])

  const grouped = useMemo(() => {
    const groups = {}
    for (const s of filtered) {
      const cat = s.category || 'Other'
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(s)
    }
    // Sort by lifecycle order
    const sorted = {}
    CATEGORY_ORDER.forEach(cat => { if (groups[cat]) sorted[cat] = groups[cat] })
    // Append any categories not in the predefined order
    Object.keys(groups).forEach(cat => { if (!sorted[cat]) sorted[cat] = groups[cat] })
    return sorted
  }, [filtered])

  const handleToggle = async (svc) => {
    if (activeServiceIds.has(svc.serviceId)) {
      // Remove
      const ps = psLookup[svc.serviceId]
      if (ps) {
        try {
          await axios.delete(`${url}/api/bid-estimation/project-services/${ps._id}`)
          onServiceRemoved()
        } catch (e) {
          console.error('Remove failed:', e)
        }
      }
    } else {
      // Add
      try {
        await axios.post(`${url}/api/bid-estimation/projects/${projectId}/project-services`, {
          serviceId: svc.serviceId,
          serviceName: svc.name,
          serviceCategory: svc.category
        })
        onServiceAdded()
      } catch (e) {
        console.error('Add failed:', e)
        if (e.response?.status === 409) alert('Service already added to this project')
      }
    }
  }

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <div style={{ width: 340, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header + search (sticky top) */}
        <div style={{ padding: 16, paddingBottom: 0 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 12 }}>
            Service Catalog
          </Typography>
          <TextField
            fullWidth size="small" variant="outlined" placeholder="Search services..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            }}
            style={{ marginBottom: 12 }}
          />
        </div>

        {/* Scrollable service list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
          {Object.entries(grouped).map(([category, svcs]) => (
            <div key={category} style={{ marginBottom: 12 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                color: '#888', padding: '4px 0', borderBottom: '1px solid #eee'
              }}>
                {category} ({svcs.length})
              </div>
              {svcs.map(svc => {
                const isActive = activeServiceIds.has(svc.serviceId)
                return (
                  <label key={svc.serviceId} style={{
                    display: 'flex', alignItems: 'center', padding: '6px 4px',
                    gap: 8, cursor: 'pointer'
                  }}>
                    <Checkbox
                      checked={isActive}
                      onChange={() => handleToggle(svc)}
                      size="small"
                      style={{ color: isActive ? '#4caf50' : undefined }}
                    />
                    <span style={{ fontWeight: isActive ? 600 : 400 }}>{svc.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#888' }}>{svc.serviceId}</span>
                  </label>
                )
              })}
            </div>
          ))}
        </div>

        {/* Sticky footer */}
        <div style={{
          padding: 12, textAlign: 'center',
          borderTop: '1px solid #e0e0e0', backgroundColor: '#fff',
          boxShadow: '0 -2px 4px rgba(0,0,0,0.05)'
        }}>
          <Button variant="contained" color="primary" size="small" onClick={onClose}>
            Done — {activeServiceIds.size} selected
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

export default ServicePicker
